import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TeamDynamicService {
  private baseUrl = environment.baseUrl + '/';

  constructor(private http: HttpClient, private auth: AuthenticationService) { }

  getOption(teamId?: string) {
    const workspaceId = localStorage.getItem('WORKSPACE_UUID');
    let headers = new HttpHeaders();

    if (workspaceId) {
      headers = headers.set('X-Workspace', workspaceId);
    }

    if (teamId) {
      headers = headers.set('X-Team', teamId);
    }

    return { headers };
  }

  getIndicatorInterval(): Observable<any> {
    const options = this.getOption();
    return this.http.get<any>(this.baseUrl + 'indicator-intervals', options)
      .pipe(
        map(res => {
            return res._embedded.indicatorIntervals;
          },
          (err: any) => {
            return err;
          }
        )
      );
  }

  getWorkspaceTeamDynamic(startDate?: any, endDate?: any): Observable<any> {
      const options = this.getOption();
      let params = new HttpParams();
  
      if (startDate && endDate) {
        params = params
          .set('startDate', startDate)
          .set('endDate', endDate);
      }
      return this.http.get<any>(this.baseUrl + 'workspace-team-dynamic', {...options, ...(params && {params})})
          .pipe(
              map(res => res),
              catchError((err: any) => {
                console.log('workspace-team-dynamic: '+JSON.stringify(err));
                return of({error: err});
              })
            );
  }
  
  getTeamsTeamDynamic(filterOptions?: { startDate?: string; endDate?: string;
                                       timedQuestionCollectionUuid?: string | null; sort?: string }): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions.startDate)
        .set('endDate', filterOptions.endDate);
    }

    if (filterOptions?.timedQuestionCollectionUuid) {
      params = params.set('timedQuestionCollection', filterOptions.timedQuestionCollectionUuid);
    }

    if (filterOptions?.sort) {
      params = params.set('sort', filterOptions.sort);
    }
    return this.http.get<any>(this.baseUrl + 'team-team-dynamics', {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-team-dynamics: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getActiveWorkspaceTeamDynamics(): Observable<any> {
    const options = this.getOption();

    return this.http.get<any>(this.baseUrl + 'active-workspace-team-dynamics', options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('active-workspace-team-dynamics: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getTeamDynamicDevelopmentScores(filterOptions?: { timedQuestionCollectionUuid?: string; startDate?: string; endDate?: string}): Observable<any> {
    const options = this.getOption();
    let url = 'team-dynamic-scores';

    if (filterOptions?.timedQuestionCollectionUuid) {
      url += `/timedQuestionCollectionUuid/${filterOptions.timedQuestionCollectionUuid}`;
    }

    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions?.startDate)
        .set('endDate', filterOptions?.endDate);
    }
    return this.http.get<any>(this.baseUrl + url, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-scores: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getWorkspaceTeamDynamicDetail(collectionUuid: string, startDate?: any, endDate?: any): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (startDate && endDate) {
      params = params
        .set('startDate', startDate)
        .set('endDate', endDate);
    }
    return this.http.get<any>(this.baseUrl + 'workspace-team-dynamic-detail/timedQuestionCollectionUuid/'+ collectionUuid, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('workspace-team-dynamic-detail: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getQuestionWorkspaceTeamDynamic(collectionUuid: string,
                                  filterOptions?: { sort?: string | null; startDate?: string; endDate?: string }): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions.startDate)
        .set('endDate', filterOptions.endDate);
    }

    if (filterOptions?.sort) {
      params = params.set('sort', filterOptions.sort);
    }
    return this.http.get<any>(this.baseUrl + 'question-workspace-team-dynamic/timedQuestionCollectionUuid/'+ collectionUuid, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('question-workspace-team-dynamic: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getStatementScaledResponses(questionUuid: string, collectionUuid?: string | null): Observable<any> {
    const options = this.getOption();
    const body = collectionUuid ? {timedQuestionCollection: collectionUuid} : {};

    return this.http.post<any>(this.baseUrl + 'team-dynamic-scaled-question-responses/questionUuid/' + questionUuid, body, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-scaled-question-responses: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getStatementOpenResponses(questionUuid: string, page: number, limit: number): Observable<any> {
    const options = this.getOption();

    return this.http.post<any>(this.baseUrl + 'team-dynamic-open-question-responses/questionUuid/' + questionUuid +
      '?page=' + page + '&limit=' + limit, {}, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-open-question-responses: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }
}
